import {
    Card,
    CardBody,
    Typography,
    Button,
    Avatar,
    Input,
    Option,
    Select,
    Dialog,
    IconButton,
    DialogBody,
    DialogHeader,
    DialogFooter, MenuHandler, Menu, MenuList, Checkbox, MenuItem,

} from "@material-tailwind/react";
import {
    ArrowLongRightIcon,
    ArrowLongUpIcon,
    ArrowLongDownIcon,
    CheckCircleIcon,
    EyeIcon,
    PencilSquareIcon,
    TrashIcon,
    XMarkIcon, ArrowDownTrayIcon, AdjustmentsHorizontalIcon
} from "@heroicons/react/24/outline";
import React, {useEffect, useMemo, useRef, useState} from "react";
import { Link } from "react-router-dom";
import getData from "../../api/axiosInstance";
import axios from "axios";
import {PlusCircleIcon} from "@heroicons/react/24/solid";
import {FunnelIcon} from "@heroicons/react/24/outline";
import {useTheme} from "../common/ThemeContext";
import {toLocalDateTimeInputValue} from "../../utils/dateTime";

export default function FiguresDashboard({ fixedName = false }) {

    // state management
    const [figuresList, setFiguresList] = useState([]);
    const [sortSettings, setSortSettings] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
    const [figureToEdit, setFigureToEdit] = useState({});
    const [deleteTimer, setDeleteTimer] = useState(null);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [newFigureData, setNewFigureData] = useState({
        name: '',
        vendor: '',
        price: 0,
        priceFees: 0,
        shippingFees: 0,
        expectedSellingPriceInRsd: '',
        orderNumber: '',
        status: '',
        purchasedDate: toLocalDateTimeInputValue()
    });
    const [searchType, setSearchType] = useState('name');
    const [selectedTypes, setSelectedTypes] = useState([]);
    const { isDark } = useTheme();
    const [isGroupDialogOpen, setIsGroupDialogOpen] = React.useState(false);
    const [groupTextInput, setGroupTextInput] = useState('');
    const [parsedGroupData, setParsedGroupData] = useState(null);

    // refs
    const timerRef = useRef(null);

    // api configuration
    const token = localStorage.getItem("token");
    const requestHeaders = useMemo(() => {
        return {
            'Authorization': `Bearer ${token}`
        };
    }, [token]);

    // data transformations
    const filteredFigures = figuresList.filter(figure => {
        const queryWords = searchTerm.toLowerCase().split(' ').filter(Boolean);

        const matchesSearch = queryWords.every(word => {
            const fieldValue = figure[searchType]?.toString().toLowerCase() || '';
            return fieldValue.includes(word);
        });

        const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(figure.status);
        const matchesType = selectedTypes.length === 0 || selectedTypes.includes(figure.type);

        return matchesSearch && matchesStatus && matchesType;
    });

    const sortedFigures = sortSettings
        ? [...filteredFigures].sort((a, b) => {
            const aName = a[sortSettings.key]?.trim() || '';
            const bName = b[sortSettings.key]?.trim() || '';

            const aStartsWithNumber = /^\d/.test(aName);
            const bStartsWithNumber = /^\d/.test(bName);

            if (aStartsWithNumber && !bStartsWithNumber) {
                return sortSettings.direction === 'ascending' ? -1 : 1;
            }
            if (!aStartsWithNumber && bStartsWithNumber) {
                return sortSettings.direction === 'ascending' ? 1 : -1;
            }

            const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
            const result = collator.compare(aName, bName);

            return sortSettings.direction === 'ascending' ? result : -result;
        })
        : filteredFigures;

    // utility functions
    const parseFigureName = (name) => {
        return name
            .toLowerCase()
            .split(/(\d+)/)
            .map(part => isNaN(part) ? part : parseInt(part, 10));
    };

    const statusLabels = {
        PLACED: "Placed",
        DISPATCHED: "Dispatched",
        RECEIVED_UK: "Received (UK)",
        IN_TRANSIT: "In Transit",
        IN_STOCK: "In Stock",
        SOLD: "Sold",
        CANCELLED: "Cancelled",
        GIVEAWAY: "Giveaway",
        IN_STOCK_PERSONAL: "Personal",
        DISPATCHED_PERSONAL: "Personal Dispatched",
    };

    const resetFigureForm = () => {
        setNewFigureData({
            name: '',
            vendor: '',
            price: 0,
            priceFees: 0,
            shippingFees: 0,
            expectedSellingPriceInRsd: '',
            orderNumber: '',
            status: '',
            purchasedDate: toLocalDateTimeInputValue()
        });
    };

    // event handlers
    const handleNewFigureInput = (e) => {
        const { name, value } = e.target;
        setNewFigureData(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleEditFigureInput = (e) => {
        const { name, value } = e.target;
        setFigureToEdit((prevData) => {
            const updatedData = {
                ...prevData,
                [name]: value,
            };
            console.log("Updated figure data:", updatedData);
            return updatedData;
        });
    };

    const exportAvailableFigures = async () => {
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        try {
            const response = await axios.get(
                `${API_URL}/figures/export_available_figures`,
                {
                    headers: requestHeaders,
                    responseType: 'blob'
                }
            );

            // Generisanje imena fajla na frontendu
            const currentDate = new Date();
            const formattedDate = currentDate.toISOString().slice(0, 10).replace(/-/g, '_');
            const filename = `${formattedDate}_available_figures.txt`;

            // Kreiranje download linka
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Čišćenje
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
            }, 100);

        } catch (err) {
            console.error("Error exporting figures:", err);
        }
    };

    const toggleAddDialog = () => {
        setNewFigureData(prev => ({
            ...prev,
            purchasedDate: toLocalDateTimeInputValue(new Date(), true)
        }));

        setIsAddDialogOpen(!isAddDialogOpen);
    };

    const toggleEditDialog = (manga) => {
        setFigureToEdit(manga);
        setIsEditDialogOpen(!isEditDialogOpen);
    };

    const handleColumnSort = (key) => {
        let direction = 'ascending';
        if (sortSettings && sortSettings.key === key && sortSettings.direction === 'ascending') {
            direction = 'descending';
        }
        setSortSettings({ key, direction });
    };

    const toggleStatus = (status) => {
        setSelectedStatuses(prev =>
            prev.includes(status)
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    const toggleType = (type) => {
        setSelectedTypes(prev =>
            prev.includes(type)
                ? prev.filter(t => t !== type)
                : [...prev, type]
        );
    };

    const handleAddFigure = async (event) => {
        event.preventDefault();

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        try {
            await axios.post(
                `${API_URL}/figures/add`,
                newFigureData,
                { headers: requestHeaders }
            );

            const response = await axios.get(
                `${API_URL}/figures/all`,
                { headers: requestHeaders }
            );

            setFiguresList(response.data);
            setIsAddDialogOpen(false);
            resetFigureForm();

        } catch (err) {
            console.error("Error adding figure:", err);
        }
    };

    const parseGroupFiguresText = (text) => {
        const lines = text.split('\n').filter(line => line.trim());

        let vendor = '';
        let orderNumber = '';
        const figures = [];
        let currentFigure = null;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();

            // Extract vendor from "##### vendor: Funko"
            const vendorMatch = line.match(/^#+\s*vendor:\s*(.+)$/i);
            if (vendorMatch) {
                vendor = vendorMatch[1].trim();
                continue;
            }

            // Extract order number from "##### order number: 1954074"
            const orderMatch = line.match(/^#+\s*order number:\s*(.+)$/i);
            if (orderMatch) {
                orderNumber = orderMatch[1].trim();
                continue;
            }

            // Check for numbered items (1., 2., etc.)
            const numberMatch = line.match(/^(\d+)\.\s*\*\*(.*?)\*\*/);
            if (numberMatch) {
                if (currentFigure) {
                    figures.push(currentFigure);
                }
                const name = numberMatch[2].trim();
                currentFigure = { name, price: null, expectedSellingPriceInRsd: null };
                continue;
            }

            // Extract price
            const priceMatch = line.match(/Cena.*?£?(\d+\.?\d*)/i);
            if (priceMatch && currentFigure && currentFigure.price === null) {
                currentFigure.price = parseFloat(priceMatch[1]);
                continue;
            }

            // Extract expected selling price in euros and convert to RSD
            const priceEuroMatch = line.match(/Moja cena.*?(\d+\.?\d*)€/i);
            if (priceEuroMatch && currentFigure && currentFigure.expectedSellingPriceInRsd === null) {
                const priceInEuro = parseFloat(priceEuroMatch[1]);
                currentFigure.expectedSellingPriceInRsd = Math.round(priceInEuro * 117);
            }
        }

        // Add the last figure
        if (currentFigure) {
            figures.push(currentFigure);
        }

        // Validate required data
        if (!vendor) {
            throw new Error('Vendor not found. Please include "##### vendor: VendorName"');
        }
        if (!orderNumber) {
            throw new Error('Order number not found. Please include "##### order number: OrderNumber"');
        }

        // Validate all figures have complete data
        const incompleteFigures = figures.filter(fig => !fig.price || !fig.expectedSellingPriceInRsd);
        if (incompleteFigures.length > 0) {
            throw new Error('Some figures are missing price or expected selling price');
        }

        return {
            vendor,
            orderNumber,
            figures
        };
    };

    const handleAddGroupFigures = async () => {
        try {
            const parsedData = parseGroupFiguresText(groupTextInput);

            const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

            const response = await axios.post(
                `${API_URL}/figures/add_group`,
                parsedData,
                { headers: requestHeaders }
            );

            // Refresh the figures list
            const figuresResponse = await axios.get(
                `${API_URL}/figures/all`,
                { headers: requestHeaders }
            );

            setFiguresList(figuresResponse.data);
            setIsGroupDialogOpen(false);
            setGroupTextInput('');
            setParsedGroupData(null);

        } catch (err) {
            console.error("Error adding group figures:", err);
            alert(`Error: ${err.message}`);
        }
    };

    const handleUpdateFigure = async (event) => {

        event.preventDefault();

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        try {
            await axios.put(
                `${API_URL}/figures/edit/${figureToEdit.id}`,
                figureToEdit,
                { headers: requestHeaders }
            );

            const response = await axios.get(
                `${API_URL}/figures/all`,
                { headers: requestHeaders }
            );

            setFiguresList(response.data);
            setIsEditDialogOpen(false);

        } catch (err) {
            console.error("Error updating figure:", err);
        }
    };

    const handleDeleteFigure = async () => {

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        if (!figureToEdit?.id) return;

        try {
            await axios.delete(
                `${API_URL}/figures/delete/${figureToEdit.id}`,
                { headers: requestHeaders }
            );

            const response = await axios.get(
                `${API_URL}/figures/all`,
                { headers: requestHeaders }
            );

            setFiguresList(response.data);
            setIsEditDialogOpen(false);
            setDeleteTimer(null);

        } catch (err) {
            console.error("Error deleting figure:", err);
        }
    };

    const startDeleteCountdown = () => {
        if (deleteTimer !== null) {
            clearInterval(timerRef.current);
            setDeleteTimer(null);
            return;
        }

        let timeLeft = 5;
        setDeleteTimer(timeLeft);

        timerRef.current = setInterval(() => {
            timeLeft -= 1;
            setDeleteTimer(timeLeft);

            if (timeLeft === 0) {
                clearInterval(timerRef.current);
                handleDeleteFigure();
            }
        }, 1000);
    };

    useEffect(() => {
        setIsLoading(true);
        getData(
            "/figures/all",
            requestHeaders,
            (data) => {
                setFiguresList(data);
                setIsLoading(false);
            },
            "Error fetching figures"
        )
    }, [requestHeaders]);

    useEffect(() => {
        if (isEditDialogOpen && figureToEdit?.id) {

            const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

            axios
                .get(
                    `${API_URL}/figures/${figureToEdit.id}`,
                    { headers: requestHeaders }
                )
                .then(response => setFigureToEdit(response.data))
                .catch(error => console.error("Error fetching figure data:", error));
        }
    }, [isEditDialogOpen, requestHeaders, figureToEdit?.id]);


    function storeScrollPosition() {
        sessionStorage.setItem('scrollPosition', window.scrollY.toString());
    }

    return (
        <Card className="w-full shadow-[0_0_15px_rgba(0,0,0,0.1)] relative overflow-hidden dark:bg-dark-900">
            <CardBody className="relative">
                <Typography variant="h5" color="blue-gray" className="mb-4 flex items-center justify-between dark:text-gray-300">
                    Figures
                    {/* Display number of filtered items */}
                    <div className="mr-4 text-sm text-gray-600 dark:text-gray-400">
                        {filteredFigures.length} items shown
                    </div>
                    <div className="flex gap-2">
                        {/* Export Button - Icon only on mobile */}
                        <Button
                            onClick={exportAvailableFigures}
                            size="md"
                            className="mv-primary-button"
                        >
                            <ArrowDownTrayIcon className="w-4 h-4 text-white" />
                            <span className="hidden md:inline">Export</span>
                        </Button>

                        {/* Add Button - Icon only on mobile */}
                        <Button
                            onClick={toggleAddDialog}
                            size="md"
                            className="mv-primary-button"
                        >
                            <PlusCircleIcon className="w-4 h-4 text-white" />
                            <span className="hidden md:inline text-white dark:text-gray-100">Add</span>
                        </Button>

                        <Button
                            onClick={() => setIsGroupDialogOpen(true)}
                            size="md"
                            className="mv-primary-button"
                        >
                            <PlusCircleIcon className="w-4 h-4 white" />
                            <span className="hidden md:inline">Add Group</span>
                        </Button>
                    </div>
                    {!fixedName && (
                        <Link to="/figures" onClick={storeScrollPosition}>
                            <Button size="sm" variant="text" className="flex gap-2 text-cyan-800">
                                View All
                                <ArrowLongRightIcon strokeWidth={2} className="w-4 h-4" />
                            </Button>
                        </Link>
                    )}
                </Typography>
                <hr className="my-2 border-blue-gray-50 dark:border-gray-700" />

                <div
                    className={`relative overflow-x-auto h-[calc(100vh-24rem)] ${
                        isLoading ? 'overflow-x-hidden' : ''
                    } scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200
                        dark:scrollbar-thumb-gray-400 dark:scrollbar-track-dark-800`}
                >
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="sticky top-0 bg-gray-50 z-10 dark:bg-dark-800">
                        <tr>
                            <th
                                onClick={() => handleColumnSort('name')}
                                className={`px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-800 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[150px] ${
                                    fixedName
                                        ? 'lg:sticky lg:top-0 lg:left-0 lg:bg-gray-50  lg:z-20 group lg:after:content-[""] lg:after:absolute lg:after:top-0 lg:after:right-[-1px] lg:after:h-full lg:after:w-[1px] lg:after:bg-gray-300 dark:lg:after:bg-gray-700 lg:after:z-20'
                                        : ''
                                }`}
                            >
                                Name
                                {sortSettings && sortSettings.key === 'name' && (
                                    sortSettings.direction === 'ascending' ? (
                                        <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    ) : (
                                        <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('status')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[115px]">
                                Status
                                {sortSettings && sortSettings.key === 'status' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('price')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]">
                                Price
                                {sortSettings && sortSettings.key === 'price' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('priceFees')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]">
                                Yug Price Fees
                                {sortSettings && sortSettings.key === 'priceFees' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('shippingFees')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[137px]">
                                Yug Shipping Fees
                                {sortSettings && sortSettings.key === 'shippingFees' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('costAfterFees')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[115px]">
                                Final Price
                                {sortSettings && sortSettings.key === 'costAfterFees' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('expectedSellingPrice')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[139px]">
                                Expected Selling Price
                                {sortSettings && sortSettings.key === 'expectedSellingPrice' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100800" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('sellingPrice')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[139px]">
                                Selling price
                                {sortSettings && sortSettings.key === 'sellingPrice' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('id')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[65px]">
                                ID
                                {sortSettings && sortSettings.key === 'id' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('yugotransportInternalNumber')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[140px]">
                                Yug Internal Number
                                {sortSettings && sortSettings.key === 'yugotransportInternalNumber' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('orderNumber')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[180px]">
                                Order Number
                                {sortSettings && sortSettings.key === 'orderNumber' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('royalMailOrderNumber')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[165px]">
                                RoyalMail Order Number
                                {sortSettings && sortSettings.key === 'royalMailOrderNumber' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('purchasedDate')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]">
                                Purchased Date
                                {sortSettings && sortSettings.key === 'purchasedDate' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('availabilityDate')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]">
                                Availability Date
                                {sortSettings && sortSettings.key === 'availabilityDate' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('saleDate')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]">
                                Sale Date
                                {sortSettings && sortSettings.key === 'saleDate' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                        </tr>
                        </thead>

                        {isLoading ? (
                            <tbody className="bg-white dark:bg-dark-900 h-[calc(100vh-24rem)]">
                            <tr>
                                <td colSpan={20} className="px-6 py-4">
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            {/* Animated Arcado Logo */}
                                            <div className="relative w-24 h-24">
                                                <img
                                                    src="/images/arcado-logo-solo-circle.png"
                                                    alt="Arcado Logo"
                                                    className="w-full h-full object-contain animate-[pulse_1.5s_ease-in-out_infinite]"
                                                />
                                                <div className="absolute inset-0 rounded-full border-4 border-cyan-900 dark:border-cyan-200 border-opacity-30 animate-[ping_1.5s_ease-in-out_infinite]"></div>
                                            </div>

                                            {/* Loading text */}
                                            <div className="text-center">
                                                <Typography variant="h6" className="text-gray-800 dark:text-gray-500 font-bold">
                                                    Loading your items...
                                                </Typography>
                                                <Typography variant="small" className="text-gray-500 dark:text-gray-200 mt-1">
                                                    We're preparing dashboard for you!
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        ) : (
                            <tbody className="bg-white divide-y dark:bg-dark-900 divide-gray-300 dark:divide-gray-700">
                            {sortedFigures.map((figure) => (
                                <tr key={figure.id}>
                                    <td
                                        className={`px-6 py-4 whitespace-nowrap relative ${
                                            fixedName
                                                ? 'lg:sticky lg:left-0 lg:bg-white dark:bg-dark-900'
                                                : ''
                                        }`}
                                    >
                                        <div className="flex items-center">
                                            <PencilSquareIcon
                                                onClick={() => toggleEditDialog(figure)}
                                                className="w-4 h-4 text-cyan-900 dark:text-cyan-200 cursor-pointer mr-2"
                                            />
                                            <div className="ml-4 relative overflow-hidden max-w-[280px] group">
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-300 truncate group-hover:whitespace-normal group-hover:overflow-visible group-hover:max-w-none">
                                                    {figure.name}
                                                </p>
                                                <p className="text-sm text-gray-500 dark:text-gray-600">
                                                    From {figure.vendor} <span className="mx-1 text-gray-800">&bull;</span>{' '}
                                                    <span className="text-teal-800 dark:text-teal-400">#{figure.orderNumber}</span>
                                                </p>
                                                {figure.purchasedDate && (
                                                    <p className="text-sm text-gray-500 dark:text-gray-600">
                                                        <span className="text-purple-800 dark:text-purple-400">
                                                            {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(figure.purchasedDate))}
                                                        </span>
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className={`text-center inline-flex px-2 text-xs font-semibold rounded-md cursor-pointer
                                                ${figure.status === "PLACED" ?
                                            "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" : ""}
                                                ${figure.status === "DISPATCHED" ?
                                            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" : ""}
                                                ${figure.status === "RECEIVED_UK" ?
                                            "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" : ""}
                                                ${figure.status === "IN_TRANSIT" ?
                                            "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" : ""}
                                                ${figure.status === "IN_STOCK" ?
                                            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : ""}
                                                ${figure.status === "SOLD" ?
                                            "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : ""}
                                                ${figure.status === "CANCELLED" ?
                                            "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" : ""}
                                                ${figure.status === "GIVEAWAY" ?
                                            "bg-black text-white dark:bg-gray-900 dark:text-gray-100" : ""}
                                                ${figure.status === "IN_STOCK_PERSONAL" ?
                                            "bg-green-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300" : ""}
                                                ${figure.status === "DISPATCHED_PERSONAL" ?
                                            "bg-yellow-100 text-lime-800 dark:bg-amber-900/30 dark:text-amber-300" : ""}  // PROMENJENO
                                                border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}>
                                            {figure.status === "PLACED" && "Placed"}
                                            {figure.status === "DISPATCHED" && "Dispatched"}
                                            {figure.status === "RECEIVED_UK" && "Received UK"}
                                            {figure.status === "IN_TRANSIT" && "In transit"}
                                            {figure.status === "IN_STOCK" && "In stock"}
                                            {figure.status === "SOLD" && "Sold"}
                                            {figure.status === "CANCELLED" && "Cancelled"}
                                            {figure.status === "GIVEAWAY" && "Giveaway"}
                                            {figure.status === "IN_STOCK_PERSONAL" && "Personal"}
                                            {figure.status === "DISPATCHED_PERSONAL" && "Dispatched P"}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <p className="text-sm text-gray-900 dark:text-gray-300">
                                            {Number.isFinite(figure.price)
                                                ? figure.price.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '/'}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            {Number.isFinite(figure.priceInRsd)
                                                ? figure.priceInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '/'}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center space-x-2">
                                            <p className="text-sm text-gray-900 dark:text-gray-300">
                                                {Number.isFinite(figure.priceFees)
                                                    ? figure.priceFees.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-600">
                                            {Number.isFinite(figure.priceFeesInRsd)
                                                ? figure.priceFeesInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '/'}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center space-x-2">
                                            <p className="text-sm text-gray-900 dark:text-gray-300">
                                                {Number.isFinite(figure.shippingFees)
                                                    ? figure.shippingFees.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-600">
                                            {Number.isFinite(figure.shippingFeesInRsd)
                                                ? figure.shippingFeesInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '/'}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <p className="text-sm text-gray-900 dark:text-gray-300 font-bold">
                                            {Number.isFinite(figure.costAfterFees)
                                                ? figure.costAfterFees.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '/'}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-600 font-bold">
                                            {Number.isFinite(figure.costAfterFeesInRsd)
                                                ? figure.costAfterFeesInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '/'}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <p className="text-sm text-gray-900 dark:text-gray-300">
                                            {Number.isFinite(figure.expectedSellingPriceInRsd)
                                                ? figure.expectedSellingPriceInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '/'}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-600">
                                            {Number.isFinite(figure.costAfterFeesInRsd) && Number.isFinite(figure.expectedSellingPriceInRsd) && figure.costAfterFeesInRsd !== 0
                                                ? (((figure.expectedSellingPriceInRsd - figure.costAfterFeesInRsd) / figure.costAfterFeesInRsd) * 100).toFixed(2) + '%'
                                                : '/'}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center space-x-2">
                                            <p className="text-sm text-gray-900 dark:text-gray-300 font-bold">
                                                {Number.isFinite(figure.sellingPriceInRsd)
                                                    ? figure.sellingPriceInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-600">
                                            {Number.isFinite(figure.marginPercent)
                                                ? figure.marginPercent.toFixed(2) + '%'
                                                : '/'}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                        {figure.id}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                        {figure.yugotransportInternalNumber}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                        {figure.orderNumber}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                        {figure.royalMailOrderNumber}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                        {figure.purchasedDate ?
                                            new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(figure.purchasedDate))
                                            : '/'
                                        }
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                        {figure.availabilityDate ?
                                            new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(figure.availabilityDate))
                                            : '/'
                                        }
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                        {figure.saleDate ?
                                            new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(figure.saleDate))
                                            : '/'
                                        }
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        )}
                    </table>
                </div>
                <div className="flex items-center mt-5 w-full gap-2 md:w-max">
                    {/* Search Type Dropdown - LEVO OD SEARCHA */}
                    <Menu placement="bottom-start">
                        <MenuHandler>
                            <Button
                                variant="filled"
                                size="md"
                                className="mv-icon-button p-0"
                            >
                                <FunnelIcon className="w-4 h-4" />
                            </Button>
                        </MenuHandler>
                        <MenuList className="dark:bg-dark-800 dark:border-gray-600">
                            {[
                                { value: 'name', label: 'Name' },
                                { value: 'vendor', label: 'Vendor' },
                                { value: 'orderNumber', label: 'Order Number' },
                                { value: 'royalMailOrderNumber', label: 'Royal Mail Order Number' },
                                { value: 'yugotransportInternalNumber', label: 'Yugotransport Internal Number' }
                            ].map((item) => (
                                <MenuItem
                                    key={item.value}
                                    onClick={() => setSearchType(item.value)}
                                    className={`flex items-center p-2 hover:bg-transparent dark:hover:bg-gray-700 ${
                                        searchType === item.value ? 'bg-cyan-100 dark:bg-cyan-800' : ''
                                    }`}
                                >
                                    <Typography
                                        color="blue-gray"
                                        className="text-sm font-normal dark:text-white"
                                    >
                                        {item.label}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>

                    {/* Search Input - SREDINA */}
                    <div className="relative">
                        <Input
                            type="search"
                            variant="outlined"
                            label={(() => {
                                const item = [
                                    { value: 'name', label: 'Name' },
                                    { value: 'vendor', label: 'Vendor' },
                                    { value: 'orderNumber', label: 'Order Number' },
                                    { value: 'royalMailOrderNumber', label: 'Royal Mail Order Number' },
                                    { value: 'yugotransportInternalNumber', label: 'Yugotransport Internal Number' }
                                ].find((i) => i.value === searchType);
                                return item ? item.label : "Title";
                            })()}
                            placeholder="Search"
                            containerProps={{
                                className: "min-w-full sm:min-w-[288px]",
                            }}
                            className="placeholder:text-blue-gray-300 dark:bg-dark-800"
                            color={isDark ? "white" : ""}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Status Filter - DESNO (ostaje isti) */}
                    <Menu placement="bottom-start">
                        <MenuHandler>
                            <Button
                                variant="filled"
                                size="md"
                                className="mv-primary-button gap-1"
                            >
                                <AdjustmentsHorizontalIcon className="w-5 h-5 text-white dark:text-gray-100" />
                                {`(${selectedStatuses.length})`}
                            </Button>
                        </MenuHandler>
                        <MenuList className="dark:bg-dark-800 dark:border-gray-600">
                            {/* Type Filter Section */}
                            {["BOOK", "GAME"].map((type) => (
                                <MenuItem
                                    key={type}
                                    className="flex items-center p-2 hover:bg-transparent dark:hover:bg-gray-700"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Checkbox
                                        ripple={false}
                                        className="bg-gray-200 hover:before:opacity-0 checked:bg-cyan-700 checked:border-cyan-700 mx-2 dark:bg-gray-600 dark:checked:bg-cyan-600 dark:checked:border-cyan-600"
                                        containerProps={{ className: "p-0" }}
                                        checked={selectedTypes.includes(type)}
                                        onChange={() => toggleType(type)}
                                        crossOrigin={undefined}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <Typography color="blue-gray" className="text-sm font-normal dark:text-white">
                                        {type === "BOOK" ? "Book" : "Game"}
                                    </Typography>
                                </MenuItem>
                            ))}

                            {/* Separator */}
                            <hr className="my-2 border-gray-300 dark:border-gray-600" />

                            {/* Status Filter Section */}
                            {[
                                "DISPATCHED", "IN_STOCK",
                                "SOLD", "CANCELLED", "GIVEAWAY", "IN_STOCK_PERSONAL", "DISPATCHED_PERSONAL"
                            ].map((status) => (
                                <MenuItem
                                    key={status}
                                    className="flex items-center p-2 hover:bg-transparent dark:hover:bg-gray-700"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Checkbox
                                        ripple={false}
                                        className="bg-gray-200 hover:before:opacity-0 checked:bg-cyan-700 checked:border-cyan-700 mx-2 dark:bg-gray-600 dark:checked:bg-cyan-600 dark:checked:border-cyan-600"
                                        containerProps={{ className: "p-0" }}
                                        checked={selectedStatuses.includes(status)}
                                        onChange={() => toggleStatus(status)}
                                        crossOrigin={undefined}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <Typography color="blue-gray" className="text-sm font-normal dark:text-white">
                                        {statusLabels[status]}
                                    </Typography>
                                </MenuItem>
                            ))}

                            {/* Reset Buttons */}
                            <MenuItem
                                onClick={() => {
                                    setSelectedTypes([]);
                                    setSelectedStatuses([]);
                                }}
                                className="p-2 hover:bg-transparent dark:hover:bg-gray-700"
                            >
                                <Typography color="blue" className="text-sm font-normal w-full text-center dark:text-cyan-400">
                                    Reset all filters
                                </Typography>
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </div>

                <Dialog size="l" open={isAddDialogOpen} handler={toggleAddDialog} className="p-4 mt-10 mb-10 flex flex-col h-[80vh] max-h-[80vh] overflow-hidden">
                    <DialogHeader className="relative m-0 block">
                        <Typography variant="h4" color="blue-gray">
                            Add new figure
                        </Typography>
                        <Typography className="mt-1 font-normal text-gray-600">
                            Import figure you bought.
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="text"
                            className="!absolute right-3.5 top-3.5"
                            onClick={toggleAddDialog}
                        >
                            <XMarkIcon className="h-4 w-4 stroke-2" />
                        </IconButton>
                    </DialogHeader>
                    <form onSubmit={handleAddFigure}>
                        <DialogBody className="space-y-4 overflow-y-auto flex-grow max-h-[50vh] mb-4">
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                    Name
                                </Typography>
                                <Input
                                    color="gray"
                                    size="lg"
                                    placeholder="eg. Naruto POP!"
                                    name="name"
                                    value={newFigureData.name}
                                    onChange={handleNewFigureInput}
                                    className="placeholder:opacity-100 focus:!border-t-gray-900"
                                    containerProps={{
                                        className: "!min-w-full",
                                    }}
                                    labelProps={{
                                        className: "hidden",
                                    }}
                                />
                            </div>
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                    Vendor
                                </Typography>
                                <Input
                                    color="gray"
                                    size="lg"
                                    placeholder="eg. Funko"
                                    name="vendor"
                                    value={newFigureData.vendor}
                                    onChange={handleNewFigureInput}
                                    className="placeholder:opacity-100 focus:!border-t-gray-900"
                                    containerProps={{
                                        className: "!min-w-full",
                                    }}
                                    labelProps={{
                                        className: "hidden",
                                    }}
                                />
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Price (£)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="price"
                                        value={newFigureData.price}
                                        onChange={handleNewFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Price Fees (£)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="priceFees"
                                        value={newFigureData.priceFees}
                                        onChange={handleNewFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>


                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Shipping Fees (£)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="shippingFees"
                                        value={newFigureData.shippingFees}
                                        onChange={handleNewFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Expected Selling Price (RSD)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="expectedSellingPriceInRsd"
                                        value={newFigureData.expectedSellingPriceInRsd}
                                        onChange={handleNewFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Order Number
                                    </Typography>
                                    <Input
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. WOB123456789GB"
                                        name="orderNumber"
                                        value={newFigureData.orderNumber}
                                        onChange={handleNewFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Purchased date
                                    </Typography>
                                    <Input
                                        type="datetime-local"
                                        color="gray"
                                        size="lg"
                                        placeholder="Select date and time"
                                        name="purchasedDate"
                                        value={newFigureData.purchasedDate}
                                        onChange={handleNewFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>


                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Status
                                    </Typography>
                                    <Select
                                        name="status" // I dalje čuvamo name
                                        className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                        onChange={(value) => handleNewFigureInput({ target: { name: 'status', value } })} // Simuliramo e.target
                                        value={newFigureData.status} // Pobrinite se da je status deo newFigureData
                                    >
                                        <Option value="DISPATCHED">Dispatched</Option>
                                        <Option value="IN_STOCK">In stock</Option>
                                        <Option value="SOLD">Sold</Option>
                                        <Option value="CANCELLED">Cancelled</Option>
                                        <Option value="GIVEAWAY">Giveaway</Option>
                                        <Option value="IN_STOCK_PERSONAL">In Stock Personal</Option>
                                        <Option value="DISPATCHED_PERSONAL">Dispatched Personal</Option>
                                    </Select>
                                </div>
                            </div>
                        </DialogBody>
                        <DialogFooter className="flex-shrink-0">
                            <Button type="submit" className="mv-primary-button ml-auto">
                                <PlusCircleIcon className="h-4 w-4" />
                                Add Figure
                            </Button>
                        </DialogFooter>
                    </form>
                </Dialog>

                <Dialog size="l" open={isGroupDialogOpen} handler={() => setIsGroupDialogOpen(!isGroupDialogOpen)} className="p-4 mt-10 mb-10 flex flex-col h-[80vh] max-h-[80vh] overflow-hidden">
                    <DialogHeader className="relative m-0 block">
                        <Typography variant="h4" color="blue-gray">
                            Add Group Figures
                        </Typography>
                        <Typography className="mt-1 font-normal text-gray-600">
                            Paste your figure group data in the specified format.
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="text"
                            className="!absolute right-3.5 top-3.5"
                            onClick={() => setIsGroupDialogOpen(false)}
                        >
                            <XMarkIcon className="h-4 w-4 stroke-2" />
                        </IconButton>
                    </DialogHeader>
                    <DialogBody className="space-y-4 overflow-y-auto flex-grow max-h-[50vh] mb-4">
                        <div>
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                Figure Data
                            </Typography>
                            <textarea
                                value={groupTextInput}
                                onChange={(e) => setGroupTextInput(e.target.value)}
                                placeholder={`##### vendor: Funko\n##### order number: 1954074\n\n1. **Figure Name**\nCena (Funko): £13.00\nMoja cena: 23€\n\n2. **Another Figure**\nCena (Funko): £11.20\nMoja cena: 21€`}
                                className="w-full h-64 p-3 border border-gray-300 rounded-lg resize-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                                rows={10}
                            />
                        </div>

                        {parsedGroupData && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <Typography variant="h6" className="mb-2">Parsed Data Preview:</Typography>
                                <Typography variant="small">Vendor: {parsedGroupData.vendor}</Typography>
                                <Typography variant="small">Order Number: {parsedGroupData.orderNumber}</Typography>
                                <Typography variant="small">Figures: {parsedGroupData.figures.length}</Typography>
                            </div>
                        )}
                    </DialogBody>
                    <DialogFooter className="flex items-center justify-between gap-2">
                        <Button
                            onClick={() => {
                                try {
                                    const data = parseGroupFiguresText(groupTextInput);
                                    setParsedGroupData(data);
                                } catch (err) {
                                    alert(`Parse Error: ${err.message}`);
                                }
                            }}
                            className="mv-secondary-button"
                        >
                            <EyeIcon className="h-4 w-4" />
                            Preview
                        </Button>
                        <Button
                            onClick={handleAddGroupFigures}
                            disabled={!parsedGroupData}
                            className="mv-primary-button"
                        >
                            <PlusCircleIcon className="h-4 w-4" />
                            Add {parsedGroupData ? `${parsedGroupData.figures.length} Figures` : 'Figures'}
                        </Button>
                    </DialogFooter>
                </Dialog>

                <Dialog size="l" open={isEditDialogOpen} handler={toggleEditDialog} className="p-4 mt-10 mb-10 flex flex-col h-[80vh] max-h-[80vh] overflow-hidden">
                    <DialogHeader className="relative m-0 block">
                        <Typography variant="h4" color="blue-gray">
                            Edit figure
                        </Typography>
                        <Typography className="mt-1 font-normal text-gray-600">
                            Update figure you bought.
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="text"
                            className="!absolute right-3.5 top-3.5"
                            onClick={toggleEditDialog}
                        >
                            <XMarkIcon className="h-4 w-4 stroke-2" />
                        </IconButton>
                    </DialogHeader>
                    <form onSubmit={handleUpdateFigure}>
                        <DialogBody className="space-y-4 overflow-y-auto flex-grow max-h-[50vh] mb-4">
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                    Name
                                </Typography>
                                <Input
                                    color="gray"
                                    size="lg"
                                    placeholder="eg. Naruto POP!"
                                    name="name"
                                    value={figureToEdit?.name || ''}
                                    onChange={handleEditFigureInput}
                                    className="placeholder:opacity-100 focus:!border-t-gray-900"
                                    containerProps={{
                                        className: "!min-w-full",
                                    }}
                                    labelProps={{
                                        className: "hidden",
                                    }}
                                />
                            </div>
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                    Vendor
                                </Typography>
                                <Input
                                    color="gray"
                                    size="lg"
                                    placeholder="eg. Funko"
                                    name="vendor"
                                    value={figureToEdit.vendor}
                                    onChange={handleEditFigureInput}
                                    className="placeholder:opacity-100 focus:!border-t-gray-900"
                                    containerProps={{
                                        className: "!min-w-full",
                                    }}
                                    labelProps={{
                                        className: "hidden",
                                    }}
                                />
                            </div>


                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Price (£)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="price"
                                        value={figureToEdit.price}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Price Fees (£)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="priceFees"
                                        value={figureToEdit.priceFees}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>


                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Shipping Fees (£)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="shippingFees"
                                        value={figureToEdit.shippingFees}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Expected Selling Price (RSD)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="expectedSellingPriceInRsd"
                                        value={figureToEdit.expectedSellingPriceInRsd}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>


                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Status
                                    </Typography>
                                    <Select
                                        name="status" // I dalje čuvamo name
                                        className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                        onChange={(value) => handleEditFigureInput({ target: { name: 'status', value } })} // Simuliramo e.target
                                        value={figureToEdit.status} // Pobrinite se da je status deo newFigureData
                                    >
                                        <Option value="DISPATCHED">Dispatched</Option>
                                        <Option value="IN_STOCK">In stock</Option>
                                        <Option value="SOLD">Sold</Option>
                                        <Option value="CANCELLED">Cancelled</Option>
                                        <Option value="GIVEAWAY">Giveaway</Option>
                                        <Option value="IN_STOCK_PERSONAL">In Stock Personal</Option>
                                        <Option value="DISPATCHED_PERSONAL">Dispatched Personal</Option>
                                    </Select>
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Selling Price (RSD)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="sellingPriceInRsd"
                                        value={figureToEdit.sellingPriceInRsd ?? ''}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Yugotransport Internal Number
                                    </Typography>
                                    <Input
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. A123B"
                                        name="yugotransportInternalNumber"
                                        value={figureToEdit.yugotransportInternalNumber}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Order Number
                                    </Typography>
                                    <Input
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. A123B"
                                        name="orderNumber"
                                        value={figureToEdit.orderNumber}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        RoyalMail Order Number
                                    </Typography>
                                    <Input
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. A123B"
                                        name="royalMailOrderNumber"
                                        value={figureToEdit.royalMailOrderNumber}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Purchased date
                                    </Typography>
                                    <Input
                                        type="datetime-local"
                                        color="gray"
                                        size="lg"
                                        placeholder="Select date and time"
                                        name="purchasedDate"
                                        value={figureToEdit.purchasedDate}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>


                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Availability date
                                    </Typography>
                                    <Input
                                        type="datetime-local"
                                        color="gray"
                                        size="lg"
                                        placeholder="Select date and time"
                                        name="availabilityDate"
                                        value={figureToEdit.availabilityDate}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Sale date
                                    </Typography>
                                    <Input
                                        type="datetime-local"
                                        color="gray"
                                        size="lg"
                                        placeholder="Select date and time"
                                        name="saleDate"
                                        value={figureToEdit.saleDate}
                                        onChange={handleEditFigureInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>
                        </DialogBody>
                        <DialogFooter className="flex items-center justify-between gap-2">
                            <Button
                            onClick={startDeleteCountdown}
                            className={deleteTimer !== null ? "mv-warning-button" : "mv-danger-button"}
                        >
                            <TrashIcon className="h-4 w-4" />
                            {deleteTimer !== null ? `Abandon (${deleteTimer})` : "Delete"}
                        </Button>
                        <Button type="submit" className="mv-primary-button">
                            <CheckCircleIcon className="h-4 w-4" />
                            Save
                        </Button>
                        </DialogFooter>
                    </form>
                </Dialog>
            </CardBody>
        </Card>
    );
}
